import React, { useEffect, useState } from "react";
import { fetchMyGloryBadges, fetchPlayerGloryBadges } from "../lib/api";

/**
 * Glory Badges grid — Pokémon-trophy aesthetic with rarity foils.
 *
 * Usage:
 *  - `<GloryBadgesGrid mine />` — pulls /glory-badges/me (includes progress
 *    + triggers a fresh evaluation server-side).
 *  - `<GloryBadgesGrid userId="…" />` — public read for someone else's
 *    profile (no progress on locked badges).
 *  - `<GloryBadgesGrid compact />` — render a horizontal strip of the latest
 *    unlocked badges (for dashboard / holo card).
 */

const RARITY_STYLES = {
  Bronze:    { ring: "border-[#14b8a6]",   text: "text-[#14b8a6]",   glow: "shadow-[0_0_10px_rgba(217,119,6,0.45)]",  bg: "bg-[#14b8a6]/15" },
  Silver:    { ring: "border-slate-300",   text: "text-slate-100",   glow: "shadow-[0_0_12px_rgba(203,213,225,0.45)]", bg: "bg-slate-300/15" },
  Gold:      { ring: "border-[#14b8a6]",  text: "text-[#14b8a6]",  glow: "shadow-[0_0_14px_rgba(253,224,71,0.55)]",  bg: "bg-[#14b8a6]/15" },
  Platinum:  { ring: "border-[#14b8a6]",    text: "text-[#14b8a6]",    glow: "shadow-[0_0_18px_rgba(103,232,249,0.55)]", bg: "bg-[#14b8a6]/15" },
  Legendary: { ring: "border-[#14b8a6]", text: "text-[#14b8a6]", glow: "shadow-[0_0_24px_rgba(232,121,249,0.65)]", bg: "bg-[#14b8a6]/15" },
};

export function GloryBadgesGrid({ mine, userId, compact, onToast, defaultExpanded = false }) {
  const [badges, setBadges] = useState([]);
  const [filter, setFilter] = useState("All");
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (mine) {
          const r = await fetchMyGloryBadges();
          if (cancelled) return;
          setBadges(r.badges || []);
          if (Array.isArray(r.newly_unlocked) && r.newly_unlocked.length > 0) {
            onToast?.(`🏅 New Glory Badge: ${r.newly_unlocked.map((b) => b.name).join(", ")}`);
          }
        } else if (userId) {
          setBadges(await fetchPlayerGloryBadges(userId));
        }
      } catch { /* 401 silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [mine, userId, onToast]);

  const unlocked = badges.filter((b) => b.unlocked);
  const inProgress = badges.filter((b) => !b.unlocked && b.progress > 0);
  const locked = badges.filter((b) => !b.unlocked && b.progress === 0);

  if (compact) {
    if (unlocked.length === 0) {
      return (
        <p className="text-[10px] text-white/40" data-testid="glory-empty-compact">
          No Glory Badges unlocked yet — play a match, add a friend, or hit a streak.
        </p>
      );
    }
    return (
      <div className="flex flex-wrap gap-2" data-testid="glory-strip">
        {unlocked.slice(0, 12).map((b) => <BadgeChip key={b.id} b={b} />)}
        {unlocked.length > 12 && (
          <span className="text-[10px] text-white/40 self-center">+{unlocked.length - 12} more</span>
        )}
      </div>
    );
  }

  const categories = ["All", "Daily Devotion", "Social Network", "Gameplay Grinder", "League Empire", "Community Legend"];
  const shown = (filter === "All" ? badges : badges.filter((b) => b.category === filter));

  return (
    <div data-testid="glory-badges-grid" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl grad-text">🏅 GLORY BADGES</h3>
          <p className="text-[11px] text-[#a3a3ac]">
            <b className="text-[#14b8a6]">{unlocked.length}</b>/{badges.length} unlocked
            {expanded ? <> · {inProgress.length} in progress</> : null}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="glory-expand-toggle"
          className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-ghost"
        >
          {expanded
            ? "HIDE LOCKED"
            : `SHOW LOCKED (${badges.length - unlocked.length})`}
        </button>
      </div>

      {/* Category filter — only useful when locked badges are visible, since
          a player with 3 unlocked badges doesn't need to filter them. */}
      {expanded && (
        <div className="flex flex-wrap gap-1.5" data-testid="glory-category-filter">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              data-testid={`glory-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[10px] tracking-widest px-2.5 py-1 rounded-lg border transition ${
                filter === c
                  ? "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]"
                  : "bg-black/40 border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {(expanded ? shown : shown.filter((b) => b.unlocked)).map((b) => (
          <BadgeCard key={b.id} b={b} />
        ))}
      </div>
      {!expanded && unlocked.length === 0 && (
        <p className="text-xs text-white/40 text-center py-6" data-testid="glory-empty-unlocked">
          No Glory Badges unlocked yet. Tap SHOW LOCKED to see what's available.
        </p>
      )}
    </div>
  );
}

function BadgeChip({ b }) {
  const s = RARITY_STYLES[b.rarity] || RARITY_STYLES.Bronze;
  return (
    <span
      data-testid={`glory-chip-${b.id}`}
      title={`${b.name} — ${b.rarity}`}
      className={`inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-1 rounded-lg border ${s.ring} ${s.bg} ${s.text} ${s.glow}`}
    >
      <i className={`fas ${b.icon}`} />
      <span className="truncate max-w-[120px]">{b.name}</span>
    </span>
  );
}

function BadgeCard({ b }) {
  const s = RARITY_STYLES[b.rarity] || RARITY_STYLES.Bronze;
  return (
    <div
      data-testid={`glory-card-${b.id}`}
      className={`relative rounded-2xl p-3 border-2 transition ${
        b.unlocked
          ? `${s.ring} ${s.bg} ${s.glow}`
          : "border-white/10 bg-black/40 opacity-70 grayscale"
      }`}
    >
      <div className="flex items-start gap-2">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${s.ring} ${b.unlocked ? s.bg : "bg-black/40"}`}>
          <i className={`fas ${b.icon} ${b.unlocked ? s.text : "text-white/40"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-xs leading-tight truncate ${b.unlocked ? "text-white" : "text-white/60"}`}>
            {b.name}
          </p>
          <p className={`text-[9px] tracking-widest ${s.text}`}>{b.rarity.toUpperCase()}</p>
        </div>
      </div>
      <p className={`mt-2 text-[10px] leading-snug ${b.unlocked ? "text-white/85" : "text-white/45"}`}>
        {b.requirement}
      </p>
      {!b.unlocked && b.progress > 0 && (
        <div className="mt-2 h-1.5 rounded-full bg-black/40 overflow-hidden">
          <div
            className={`h-full ${s.bg.replace("/15", "/50")}`}
            style={{ width: `${Math.round(b.progress * 100)}%` }}
          />
        </div>
      )}
      {b.unlocked && (
        <p className="mt-2 text-[9px] italic text-white/55">"{b.flavor}"</p>
      )}
    </div>
  );
}

export default GloryBadgesGrid;
